import React, { useMemo, useState } from 'react';
import { History, X, Loader2 } from 'lucide-react';
import { useRadarHistory } from '../../lib/useRadarHistory';
import { CoinTable, Bar, RecentList, RateCell, pct } from './RadarHistoryParts';

const DAYS = [7, 30, 90];

const Chip = ({ label, value, testid, cls = 'text-slate-50' }) => (
    <div data-testid={testid} className="rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2">
        <div className="text-[9px] uppercase tracking-wider text-slate-400">{label}</div>
        <div className={`font-mono text-sm font-semibold ${cls}`}>{value}</div>
    </div>
);

const Summary = ({ s }) => (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="radar-history-summary-chips">
        <Chip label="Sonuçlanan sinyal" value={s.n} testid="radar-history-total" />
        <Chip label="Tuttu" value={s.n ? `${s.realized}/${s.n} · %${s.rate_pct}` : '—'} testid="radar-history-rate" cls={s.rate_pct >= 50 ? 'text-emerald-300' : 'text-amber-300'} />
        <Chip label="Ort. en iyi hareket" value={pct(s.avg_mfe_pct, 2)} testid="radar-history-mfe" />
        <Chip label="Önerilen kaldıraçla ort." value={pct(s.avg_lev_pnl_pct)} testid="radar-history-levpnl" cls={s.avg_lev_pnl_pct > 0 ? 'text-emerald-300' : 'text-rose-300'} />
        <Chip label="İzleniyor (24s dolmadı)" value={s.open} testid="radar-history-open" cls="text-sky-300" />
    </div>
);

const HARD = new Set(['pattern', 'oi', 'volume', 'breakout']);
const weightTag = (c, base) => {
    if (!c || c.n < 10 || c.rate_pct === null || base === null || base === undefined) return null;
    const d = c.rate_pct - base;
    return d >= 8 ? ['tutarlı', 'text-emerald-300 border-emerald-500/40'] : d <= -8 ? ['zayıf', 'text-rose-300 border-rose-500/40'] : ['nötr', 'text-slate-400 border-slate-600'];
};

const CheckLabel = ({ c, base }) => {
    const w = weightTag(c, base);
    return (
        <span className="inline-flex items-center gap-1">
            {c.label}
            {HARD.has(c.key) && <span className="text-[8px] uppercase tracking-wider text-amber-300/80 border border-amber-400/30 rounded-sm px-1">sert kanıt</span>}
            {w && <span data-testid={`radar-history-check-weight-${c.key}`} className={`text-[8px] border rounded-sm px-1 ${w[1]}`}>{w[0]}</span>}
        </span>
    );
};

const Setups = ({ st, base }) => (
    <div className="space-y-3" data-testid="radar-history-setups">
        <div className="text-[10px] uppercase tracking-wider text-sky-200 font-semibold">Hangi kurulum tutuyor?</div>
        <div className="grid grid-cols-2 gap-2 text-[11px]">
            {['long', 'short'].map((d) => (
                <div key={d} data-testid={`radar-history-dir-${d}`} className="rounded-md border border-slate-700 bg-slate-900/50 px-2 py-1.5 flex justify-between">
                    <span className={d === 'long' ? 'text-emerald-300' : 'text-rose-300'}>{d.toUpperCase()}</span>
                    <RateCell s={st.by_direction[d]} />
                </div>
            ))}
        </div>
        <div className="space-y-1.5">
            <div className="text-[10px] text-slate-400">Teyit sayısına göre</div>
            {st.by_passed.map((p) => <Bar key={p.passed} label={`${p.passed}/7 teyit`} s={p} testid={`radar-history-passed-${p.passed}`} />)}
        </div>
        <div className="space-y-1.5">
            <div className="text-[10px] text-slate-400">
                Teyit geçtiğinde tutma oranı <span className="text-slate-500">({st.tracked} sinyalde teyit verisi var · genel %{base ?? '—'} · tutarlı = genelden ≥8 puan iyi, 10+ sinyal)</span>
            </div>
            {st.by_check.map((c) => <Bar key={c.key} label={<CheckLabel c={c} base={base} />} s={c} testid={`radar-history-check-${c.key}`} />)}
        </div>
        {st.combos.length > 0 && (
            <div className="space-y-1" data-testid="radar-history-combos">
                <div className="text-[10px] text-slate-400">En sık teyit kombinasyonları</div>
                {st.combos.map((c) => (
                    <div key={c.keys.join('+')} className="flex items-center justify-between gap-2 text-[10px] border-b border-slate-800 py-1">
                        <span className="text-slate-300">{c.labels.join(' + ')}</span>
                        <RateCell s={c} />
                    </div>
                ))}
            </div>
        )}
    </div>
);

export default function RadarHistoryPanel() {
    const [days, setDays] = useState(30);
    const [symbol, setSymbol] = useState(null);
    const [q, setQ] = useState('');
    const { data, loading, error } = useRadarHistory(days, symbol);
    const coins = useMemo(() => {
        const t = q.trim().toUpperCase();
        return (data?.by_symbol || []).filter((r) => !t || r.symbol.includes(t));
    }, [data, q]);

    return (
        <div data-testid="radar-history-panel" className="border border-sky-500/20 bg-slate-800 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-700 flex items-center gap-3 flex-wrap">
                <History className="h-4 w-4 text-sky-400" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-sky-200 font-semibold">Radar Sonuç Geçmişi</span>
                {symbol && (
                    <button data-testid="radar-history-clear-symbol" onClick={() => setSymbol(null)} className="flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-sky-500/20 text-sky-100 border border-sky-500/40 hover:bg-sky-500/30">
                        {symbol.replace('USDT', '')} <X className="h-3 w-3" />
                    </button>
                )}
                {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
                <div className="ml-auto flex items-center gap-2">
                    <input
                        data-testid="radar-history-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Coin ara (BTC, ARB…)"
                        className="h-7 w-36 rounded-sm bg-slate-900 border border-slate-700 px-2 text-[11px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-sky-500"
                    />
                    <div className="flex rounded-sm border border-slate-700 overflow-hidden" data-testid="radar-history-days">
                        {DAYS.map((d) => (
                            <button key={d} data-testid={`radar-history-days-${d}`} onClick={() => setDays(d)} className={`px-2 h-7 text-[10px] font-mono transition-colors ${days === d ? 'bg-sky-500 text-slate-950 font-bold' : 'text-slate-300 hover:bg-slate-700'}`}>
                                {d}g
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            {error && !data && <div data-testid="radar-history-error" className="px-4 py-6 text-xs text-rose-300">{error}</div>}
            {data && (
                <div className="p-4 space-y-4">
                    <Summary s={data.summary} />
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                        <div className="lg:col-span-3 space-y-2">
                            <div className="text-[10px] uppercase tracking-wider text-sky-200 font-semibold">Coin bazında · satıra tıkla → sadece o coin</div>
                            {coins.length ? <CoinTable rows={coins} onPick={setSymbol} active={symbol} /> : <div data-testid="radar-history-empty" className="text-[11px] text-slate-400 py-4">Bu aralıkta radar sinyali yok.</div>}
                        </div>
                        <div className="lg:col-span-2"><Setups st={data.setups} base={data.summary?.rate_pct} /></div>
                    </div>
                    {data.recent.length > 0 && (
                        <div className="space-y-1">
                            <div className="text-[10px] uppercase tracking-wider text-sky-200 font-semibold">Son sinyaller{symbol ? ` · ${symbol.replace('USDT', '')}` : ''}</div>
                            <RecentList items={data.recent} />
                        </div>
                    )}
                    <p className="text-[10px] text-slate-500 leading-snug" data-testid="radar-history-footnote">
                        TUTTU = 24 saat içinde beklenen yönde ≥%{data.target_move_pct} hareket (önce ters yönde %{data.adverse_move_pct} gitmeden).
                        Kaldıraçlı K/Z = bu hareket × o anki önerilen kaldıraç (eski kayıtlarda kaldıraç yoksa 1x). Yatırım tavsiyesi değildir.
                    </p>
                </div>
            )}
        </div>
    );
}
